import * as React from 'react';
import {CalendarDays, SquareX} from 'lucide-react';
import {cn} from '../../../lib/cn';

const DEFAULT_PLACEHOLDER = '00-00-0000';

type DateRangeInputsProps = {
  fromId: string;
  toId: string;
  name?: string;
  labelId: string;
  fromText: string;
  toText: string;
  fromPlaceholder?: string;
  toPlaceholder?: string;
  fromInputRef: React.RefObject<HTMLInputElement>;
  toInputRef: React.RefObject<HTMLInputElement>;
  isEmpty: boolean;
  isFocused: boolean;
  isBlocked: boolean;
  isInvalid: boolean;
  loading?: boolean;
  readOnly?: boolean;
  hideClearDates?: boolean;
  hideCalendarIcon?: boolean;
  isOpen: boolean;
  focusedInput: 'from' | 'to' | null;
  clearLabel: string;
  openCalendarLabel: string;
  onFromTextChange: (text: string) => void;
  onToTextChange: (text: string) => void;
  onFromFocus: React.FocusEventHandler<HTMLInputElement>;
  onToFocus: React.FocusEventHandler<HTMLInputElement>;
  onFromBlur: () => void;
  onToBlur: () => void;
  onRowClick: () => void;
  onReset: (event: React.MouseEvent) => void;
  onToggleCalendar: () => void;
};

const inputBaseClass =
  'm-0 box-border h-full w-full min-w-0 border-0 bg-transparent text-[16px] font-medium leading-5 text-[var(--chekin-color-brand-navy)] outline-none placeholder:text-[length:var(--field-placeholder-font-size)] placeholder:font-[var(--field-placeholder-font-weight)] placeholder:text-[var(--chekin-color-gray-1)]';

const iconButtonClass =
  'flex h-5 w-5 items-center justify-center rounded-[3px] border-0 bg-transparent p-0 text-[var(--chekin-neutral-500)] outline-none hover:shadow-[0_3px_3px_#0f477734] disabled:cursor-not-allowed';

export function DateRangeInputs({
  fromId,
  toId,
  name,
  labelId,
  fromText,
  toText,
  fromPlaceholder = DEFAULT_PLACEHOLDER,
  toPlaceholder = DEFAULT_PLACEHOLDER,
  fromInputRef,
  toInputRef,
  isEmpty,
  isFocused,
  isBlocked,
  isInvalid,
  loading,
  readOnly,
  hideClearDates,
  hideCalendarIcon,
  isOpen,
  focusedInput,
  clearLabel,
  openCalendarLabel,
  onFromTextChange,
  onToTextChange,
  onFromFocus,
  onToFocus,
  onFromBlur,
  onToBlur,
  onRowClick,
  onReset,
  onToggleCalendar,
}: DateRangeInputsProps) {
  const inputClass = cn(
    inputBaseClass,
    isBlocked && 'cursor-not-allowed',
    loading && 'cursor-progress',
  );

  return (
    <div
      className={cn(
        'relative box-border flex h-12 w-full items-center rounded-[6px] pr-2 text-[16px] font-medium text-[var(--chekin-color-brand-navy)] transition-colors duration-200',
        isEmpty && !isFocused && 'bg-[var(--empty-field-background)]',
      )}
      onClick={onRowClick}
    >
      <input
        ref={fromInputRef}
        id={fromId}
        name={name ? `${name}-from` : undefined}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder={fromPlaceholder}
        value={fromText}
        disabled={isBlocked}
        readOnly={readOnly}
        aria-invalid={isInvalid}
        aria-labelledby={labelId}
        onChange={event => onFromTextChange(event.target.value)}
        onFocus={onFromFocus}
        onBlur={onFromBlur}
        className={cn(
          inputClass,
          'max-w-[110px] pl-[14px] pr-0',
          isEmpty && !isFocused && 'placeholder:invisible',
        )}
      />
      <span
        aria-hidden="true"
        className={cn(
          'shrink-0 px-2 text-[18px] leading-none text-[var(--chekin-color-gray-2)]',
          isEmpty && !isFocused && 'invisible',
        )}
      >
        →
      </span>
      <input
        ref={toInputRef}
        id={toId}
        name={name ? `${name}-to` : undefined}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder={toPlaceholder}
        value={toText}
        disabled={isBlocked}
        readOnly={readOnly}
        aria-invalid={isInvalid}
        aria-labelledby={labelId}
        onChange={event => onToTextChange(event.target.value)}
        onFocus={onToFocus}
        onBlur={onToBlur}
        className={cn(
          inputClass,
          'px-0',
          isEmpty && !isFocused && 'placeholder:invisible',
        )}
      />
      <span className="ml-auto flex shrink-0 items-center gap-2 pl-2 text-[var(--chekin-color-gray-2)]">
        {!readOnly && !hideClearDates && !isEmpty && (
          <button
            type="button"
            disabled={isBlocked}
            onClick={onReset}
            className={iconButtonClass}
            aria-label={clearLabel}
          >
            <SquareX
              size={16}
              fill="var(--chekin-neutral-500)"
              color="#f8f8f8"
              strokeWidth={1.8}
            />
          </button>
        )}
        {!readOnly && !hideCalendarIcon && (
          <button
            type="button"
            disabled={isBlocked}
            onClick={onToggleCalendar}
            aria-pressed={isOpen}
            className={cn(
              'flex h-6 w-6 items-center justify-center rounded-[3px] border-0 bg-transparent p-0 outline-none disabled:cursor-not-allowed',
              focusedInput !== null || isOpen
                ? 'text-[var(--chekin-color-brand-blue)]'
                : 'text-[var(--chekin-color-gray-2)]',
            )}
            aria-label={openCalendarLabel}
          >
            <CalendarDays size={18} />
          </button>
        )}
      </span>
    </div>
  );
}
